import { useState, type FormEvent } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";

import { useAuth, useLogin, useRegister } from "../api/hooks";
import type { AuthState } from "../api/types";
import { Loading } from "../components/Layout";
import { Splash } from "../components/Splash";

/**
 * The welcome page: sign in, or create an account. Before the first account exists, creating one comes first
 * (it becomes the admin); after that, signing up asks an admin for an account.
 */
export function LoginPage() {
  const auth = useAuth();
  const [params] = useSearchParams();
  const next = params.get("next");
  const dest = next && next.startsWith("/") && !next.startsWith("//") ? next : "/";

  if (auth.isPending)
    return (
      <main className="splash">
        <Loading />
      </main>
    );
  if (auth.data?.user) return <Navigate to={dest} replace />;

  return <Splash>{auth.data && <AuthCard auth={auth.data} dest={dest} />}</Splash>;
}

function AuthCard({ auth, dest }: { auth: AuthState; dest: string }) {
  const [mode, setMode] = useState<"signin" | "register">(auth.has_accounts ? "signin" : "register");
  const tab = (m: typeof mode, label: string) => (
    <button
      type="button"
      role="tab"
      aria-selected={mode === m}
      className={mode === m ? "is-on" : undefined}
      onClick={() => setMode(m)}
    >
      {label}
    </button>
  );
  return (
    <div className="splash-card panel">
      <div className="splash-tabs" role="tablist" aria-label="Sign in or create an account">
        {tab("signin", "Sign in")}
        {tab("register", "Create account")}
      </div>
      {mode === "signin" ? (
        <SignIn auth={auth} dest={dest} onRegister={() => setMode("register")} />
      ) : (
        <Register auth={auth} dest={dest} />
      )}
    </div>
  );
}

function SignIn({ auth, dest, onRegister }: { auth: AuthState; dest: string; onRegister: () => void }) {
  const login = useLogin();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  if (!auth.has_accounts)
    return (
      <div className="splash-form">
        <h2>Sign in</h2>
        <p className="muted">There&apos;s no account here yet. Create one to get started.</p>
        <button className="btn" type="button" onClick={onRegister}>
          Create account
        </button>
      </div>
    );

  const submit = (e: FormEvent) => {
    e.preventDefault();
    login.mutate({ email, password }, { onSuccess: () => navigate(dest, { replace: true }) });
  };
  return (
    <form className="splash-form" onSubmit={submit}>
      <h2>Welcome back</h2>
      <div>
        <label htmlFor="login-email">Email</label>
        <input
          id="login-email"
          type="email"
          required
          autoFocus
          autoComplete="username"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </div>
      <div>
        <label htmlFor="login-pw">Password</label>
        <input
          id="login-pw"
          type="password"
          required
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
      </div>
      {login.error && (
        <p className="small error-text" role="alert">
          {login.error.message}
        </p>
      )}
      <button className="btn" type="submit" disabled={login.isPending}>
        Sign in
      </button>
      <details className="small forgot">
        <summary>Forgotten your password?</summary>
        <p className="muted">Ask an admin for a reset link. It lets you choose a new password.</p>
      </details>
    </form>
  );
}

function Register({ auth, dest }: { auth: AuthState; dest: string }) {
  const register = useRegister();
  const navigate = useNavigate();
  const [f, setF] = useState({ display_name: "", email: "", password: "", confirm: "", site_password: "" });
  const [problem, setProblem] = useState("");
  const [requested, setRequested] = useState(false);
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) =>
    setF((x) => ({ ...x, [k]: e.target.value }));
  const first = !auth.has_accounts;

  if (requested)
    return (
      <div className="splash-form">
        <h2>Request sent</h2>
        <p className="muted">
          Thanks, {f.display_name.trim()}. An admin needs to approve your account; once they have, sign in
          with {f.email.trim()}.
        </p>
      </div>
    );
  if (!auth.registration_open)
    return (
      <div className="splash-form">
        <h2>Ask for an account</h2>
        <p className="muted">Sign-ups are closed at the moment. Ask whoever runs this Work Tracker.</p>
      </div>
    );

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (f.password !== f.confirm) return setProblem("The two passwords don't match.");
    setProblem("");
    register.mutate(
      {
        display_name: f.display_name,
        email: f.email,
        password: f.password,
        ...(first && auth.password_required ? { site_password: f.site_password } : {}),
      },
      { onSuccess: (state) => (state.user ? navigate(dest, { replace: true }) : setRequested(true)) },
    );
  };
  const error = problem || register.error?.message;
  return (
    <form className="splash-form" onSubmit={submit}>
      <h2>{first ? "Create your account" : "Ask for an account"}</h2>
      <p className="small muted">
        {first
          ? "You're the first here, so you'll be the admin, and everything already in this Work Tracker will be yours."
          : "An admin approves new accounts. You'll be able to sign in once they have."}
      </p>
      <div>
        <label htmlFor="reg-name">Display name</label>
        <input
          id="reg-name"
          required
          autoFocus
          autoComplete="name"
          maxLength={80}
          value={f.display_name}
          onChange={set("display_name")}
        />
      </div>
      <div>
        <label htmlFor="reg-email">Email</label>
        <input
          id="reg-email"
          type="email"
          required
          autoComplete="email"
          value={f.email}
          onChange={set("email")}
        />
        <p className="small muted field-hint">You&apos;ll use this to sign in.</p>
      </div>
      <div className="splash-pair">
        <div>
          <label htmlFor="reg-pw">Password</label>
          <input
            id="reg-pw"
            type="password"
            required
            minLength={8}
            autoComplete="new-password"
            value={f.password}
            onChange={set("password")}
          />
        </div>
        <div>
          <label htmlFor="reg-pw2">Confirm password</label>
          <input
            id="reg-pw2"
            type="password"
            required
            autoComplete="new-password"
            value={f.confirm}
            onChange={set("confirm")}
          />
        </div>
      </div>
      <p className="small muted field-hint">At least 8 characters.</p>
      {first && auth.password_required && (
        <div>
          <label htmlFor="reg-site">Site password</label>
          <input
            id="reg-site"
            type="password"
            required
            autoComplete="off"
            value={f.site_password}
            onChange={set("site_password")}
          />
          <p className="small muted field-hint">
            The password this Work Tracker was set up with (APP_PASSWORD).
          </p>
        </div>
      )}
      {error && (
        <p className="small error-text" role="alert">
          {error}
        </p>
      )}
      <button className="btn" type="submit" disabled={register.isPending}>
        {first ? "Create account" : "Send request"}
      </button>
    </form>
  );
}
